import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { printReceiptRequestCode, printReceiptRequestCodeResponse } from "./TransactionRequestData/PrintReceiptRequestCode";

const languageMap = {
    curl: 'bash',
    java: 'java',
    "C#": 'csharp',
    python: 'python',
    JavaScript: 'javascript'
};

const PrintReceiptComponent = () => {
    const [selectedLanguagePrintReceiptRequest, setSelectedLanguagePrintReceiptRequest] = useState('curl');
    const [copyStatus, setCopyStatus] = useState({ type: '', isCopied: false });

    const handleSelectedCode = (language) => {
        setSelectedLanguagePrintReceiptRequest(language);
    };

    const handleCopyClick = (type) => {
        const textToCopy = type === 'printReceiptRequest' ? printReceiptRequestCode[selectedLanguagePrintReceiptRequest].request : printReceiptRequestCodeResponse;
        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                setCopyStatus({ type, isCopied: true });
                setTimeout(() => {
                    setCopyStatus({ type: '', isCopied: false });
                }, 1000);
            })
            .catch((error) => {
                console.error('Error copying text: ', error);
                alert('Failed to copy text. Please try again.');
            });
    };


    return (
        <section className="docs-section" id="print-receipt">

            <div className="row flexbox">

                <div className="col-md-5">
                    <h2 className="section-heading">Print Request</h2>
                    <p><span className="badge badge-secondary back-green">POST</span><span className="api"> /in-store/api/v1/printData</span></p>
                    <p>Service to send data which is then printed by the terminal.</p>

                    <div class="alert alert-primary" role="alert">
                    Printing functionality is supported on terminals <b>C100</b> and <b>C150</b>
                    </div>

                    <h4>Request Body</h4>

                    <div className="property-tab col-md-10 border-bottom">
                        <p required><span className="badge badge-secondary name">terminalId</span> &nbsp; &nbsp; <b>string</b></p>
                        <p className="description">Terminal ID</p>
                    </div>
                    <div className="property-tab col-md-10 border-bottom">
                        <p required><span className="badge badge-secondary name">htmlContent</span> &nbsp; &nbsp; <b>string</b></p>
                        <p className="description">HTML content should be provided as valid JSON value</p>
                    </div>

                    <br />
                    <div className="response-codes">

                        <h4>Print Status</h4>

                        <div className="table-wrapper">

                            <table style={{ borderTop: '1px solid #e8e8e8' }}>
                                <tbody>
                                    <tr>
                                        <td>0 Success</td>
                                        <td>The print operation completed successfully.</td>
                                    </tr>
                                    <tr>
                                        <td>1 Busy</td>
                                        <td>The terminal is currently busy with another task.</td>
                                    </tr>
                                    <tr>
                                        <td>2 Out of Paper</td>
                                        <td>The terminal is out of paper and cannot complete the request.</td>
                                    </tr>
                                    <tr>
                                        <td>3 Low Battery</td>
                                        <td>The terminal's battery is low, affecting the print operation.</td>
                                    </tr>
                                    <tr>
                                        <td>4 Error</td>
                                        <td>An error occurred during the print process.</td>
                                    </tr>
                                    <tr>
                                        <td>5 Cancelled</td>
                                        <td>The print operation was cancelled by the user or system.</td>
                                    </tr>
                                    <tr>
                                        <td>6 Timeout</td>
                                        <td>The print operation timed out due to a delay or issue.</td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>

                    </div>
                    <br />

                    <div className="response-codes">

                        <h4>Response Codes</h4>

                        <div className="table-wrapper">

                            <h6 className="header">HTTP Status code summary</h6>

                            <table>
                                <tbody>
                                    <tr>
                                        <td>200 Success</td>
                                        <td>Print Data request is successfully processed</td>
                                    </tr>
                                    <tr>
                                        <td>201 Created</td>
                                        <td>Print Data request is properly created and notified to terminal</td>
                                    </tr>
                                    <tr>
                                        <td>400 Bad Request</td>
                                        <td>Bad request format or validation failed for incorrect data formats</td>
                                    </tr>
                                    <tr>
                                        <td>401 Unauthorized</td>
                                        <td>Request requires authentication, and the provided credentials are either missing, invalid, or insufficient, hence send a valid merchant access token (Bearer) issued by Westpay in the "Authorization" header</td>
                                    </tr>
                                    <tr>
                                        <td>422 Client Error</td>
                                        <td>Un-processable request. Most probably this happens when a validation error occurs while processing the print data request</td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>

                    </div>
                </div>

                <div className="col-md-7 code-segments">
                    <div className="docs-code-block">
                        <pre tabIndex="-1" className="shadow-lg rounded">
                            <code className="language-dockerfile hljs">
                                <div className="row code-header">
                                    <div className="col-md-10 languages">
                                        <span>Request</span>
                                        {Object.keys(printReceiptRequestCode).map(language => (
                                            <React.Fragment key={language}>
                                                <input type="radio" name="langPrintReceiptRequest" id={`${language}PrintReceiptRequest`} onClick={() => handleSelectedCode(language)} checked={selectedLanguagePrintReceiptRequest === language} />
                                                <label htmlFor={`${language}PrintReceiptRequest`} className="language">{language}</label>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <div className="col-md-2">
                                        {copyStatus.type !== 'printReceiptRequest' && <span className="copy-btn" title="Copy" onClick={() => handleCopyClick("printReceiptRequest")}><i className="fa fa-copy"></i></span>}
                                        {copyStatus.type === 'printReceiptRequest' && copyStatus.isCopied && <div className="copy-message">Copied &#x2713;</div>}
                                    </div>
                                </div>
                                <div className="code-section">
                                    <SyntaxHighlighter language={languageMap[selectedLanguagePrintReceiptRequest]} style={dracula} showLineNumbers>
                                        {printReceiptRequestCode[selectedLanguagePrintReceiptRequest].request}
                                    </SyntaxHighlighter>
                                </div>
                            </code>
                        </pre>
                    </div>
                    <div className="docs-code-block">
                        <pre tabIndex="-1" className="shadow-lg rounded">
                            <code className="language-dockerfile hljs">
                                <div className="row code-header">
                                    <div className="col-md-10 languages">
                                        <span>Response</span>
                                    </div>
                                    <div className="col-md-2">
                                        {copyStatus.type !== 'printReceiptRequestResponse' && <span className="copy-btn" title="Copy" onClick={() => handleCopyClick("printReceiptRequestResponse")}><i className="fa fa-copy"></i></span>}
                                        {copyStatus.type === 'printReceiptRequestResponse' && copyStatus.isCopied && <div className="copy-message">Copied &#x2713;</div>}
                                    </div>
                                </div>
                                <div className="code-section">
                                    <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                        {printReceiptRequestCodeResponse}
                                    </SyntaxHighlighter>
                                </div>
                            </code>
                        </pre>
                    </div>
                </div>

            </div>

        </section>
    );
};


export default PrintReceiptComponent;
